/**
 * Moduli M7 e M8: amministrazione, registro eventi, GDPR, TOTP, metriche.
 */
import { z } from 'zod';
import { emailSchema, passwordSchema } from './auth.js';
import { RUOLI } from './ruoli.js';
import { paginazioneSchema } from './percorsi.js';

const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });

// ---------------------------------------------------------------- Contatti operativi (§15)
export const RUOLI_CONTATTO = ['organizzatore', 'segreteria', 'responsabile_tecnico', 'devops', 'regia', 'dpo', 'altro'] as const;
export const contattoInputSchema = z.object({
  ruolo: z.enum(RUOLI_CONTATTO),
  nome: z.string().trim().min(2, 'Scrivi nome e cognome.').max(120),
  email: emailSchema.optional().or(z.literal('')),
  telefono: z.string().trim().regex(/^\+\d{6,15}$/, 'Numero in formato internazionale, es. +39...').optional().or(z.literal('')),
  riceveReport: z.boolean().default(false),
  riceveAvvisi: z.boolean().default(false),
  riceveChiamate: z.boolean().default(false),
  attivoDal: dataIso.optional(),
  attivoAl: dataIso.nullable().optional(),
  note: z.string().trim().max(500).optional().or(z.literal('')),
});
export type ContattoInput = z.infer<typeof contattoInputSchema>;
export const contattoSchema = contattoInputSchema.extend({ id, attivoDal: dataIso, attivoAl: dataIso.nullable(), attivo: z.boolean() });
export type Contatto = z.infer<typeof contattoSchema>;
/** Avvisi di configurazione: almeno un destinatario dei report e due per le chiamate. */
export const avvisiContattiSchema = z.object({ report: z.boolean(), chiamate: z.boolean() });

// ---------------------------------------------------------------- Email inviate
export const STATI_EMAIL = ['in_coda', 'inviata', 'errore'] as const;
export const emailInviataSchema = z.object({
  id,
  to: z.string(),
  template: z.string(),
  stato: z.enum(STATI_EMAIL),
  errore: z.string().nullable(),
  at: dataIso,
  /** oggetto, se noto */
  oggetto: z.string().nullable(),
  reinviabile: z.boolean(),
});
export type EmailInviata = z.infer<typeof emailInviataSchema>;
export const ricercaEmailSchema = paginazioneSchema.extend({ q: z.string().trim().max(120).optional(), stato: z.enum(STATI_EMAIL).optional(), template: z.string().max(60).optional() });

// ---------------------------------------------------------------- Utenti (admin, dettaglio)
export const utenteDettaglioSchema = z.object({
  id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  ruolo: z.enum(RUOLI),
  stato: z.enum(['in_verifica', 'attivo', 'sospeso']),
  ordine: z.string().nullable(),
  numeroAlbo: z.string().nullable(),
  /** in chiaro: solo qui, con evento nel registro */
  codiceFiscale: z.string(),
  totpAttivo: z.boolean(),
  ultimoAccessoAt: dataIso.nullable(),
  createdAt: dataIso,
  iscrizioni: z.array(z.object({ percorsoId: id, percorsoTitolo: z.string(), stato: z.string(), iscrittoDal: dataIso })),
  webinar: z.array(
    z.object({
      webinarId: id,
      titolo: z.string(),
      inizioPrevisto: dataIso,
      conferma: z.string().nullable(),
      modalita: z.string().nullable(),
      minutiTotali: z.number().int().nullable(),
      percentuale: z.number().nullable(),
      esitoTest: z.string().nullable(),
      attestatoCodice: z.string().nullable(),
    }),
  ),
});
export type UtenteDettaglio = z.infer<typeof utenteDettaglioSchema>;
export const modificaUtenteSchema = z.object({
  nome: z.string().trim().min(1).max(100),
  cognome: z.string().trim().min(1).max(100),
  email: emailSchema,
  ordine: z.string().trim().max(200).optional().or(z.literal('')),
  numeroAlbo: z.string().trim().max(50).optional().or(z.literal('')),
  /** motivo della modifica, nel registro */
  motivo: z.string().trim().min(3, 'Scrivi il motivo della modifica.').max(300),
});
export const sospensioneSchema = z.object({ motivo: z.string().trim().min(3).max(300) });

// ---------------------------------------------------------------- Impostazioni
export const CATEGORIE_RETENTION = ['presenze', 'esiti_test', 'attestati', 'domande_pubblico', 'log_accessi', 'email_log'] as const;
export const impostazioniSchema = z.object({
  retention: z.array(z.object({ tipoDato: z.enum(CATEGORIE_RETENTION), giorni: z.number().int().min(30).max(3650), descrizione: z.string().nullable() })),
  webinarDefault: z.object({ sogliaPresenzaPct: z.number().int().min(1).max(100), testDurataMin: z.number().int().min(1).max(120), testFinestraOre: z.number().int().min(1).max(72), testCorretteMinime: z.number().int().min(1).max(20) }),
  domande: z.object({ maxPerWebinar: z.number().int().min(1).max(100), ogniSecondi: z.number().int().min(10).max(600) }),
  /** prima cancellazione automatica prevista, se c'è qualcosa in scadenza */
  prossimaCancellazione: z.object({ tipoDato: z.string(), quando: dataIso, elementi: z.number().int() }).nullable(),
});
export type Impostazioni = z.infer<typeof impostazioniSchema>;
export const modificaImpostazioniSchema = impostazioniSchema.omit({ prossimaCancellazione: true }).partial();

// ---------------------------------------------------------------- Registro eventi
export const eventoRegistroSchema = z.object({
  id,
  at: dataIso,
  actorId: id.nullable(),
  actorNome: z.string().nullable(),
  actorRole: z.string().nullable(),
  azione: z.string(),
  soggettoTipo: z.string().nullable(),
  soggettoId: z.string().nullable(),
  dettagli: z.record(z.unknown()).nullable(),
  ip: z.string().nullable(),
  hash: z.string(),
});
export type EventoRegistro = z.infer<typeof eventoRegistroSchema>;
export const ricercaRegistroSchema = paginazioneSchema.extend({
  q: z.string().trim().max(120).optional(),
  azione: z.string().trim().max(60).optional(),
  actorId: id.optional(),
  soggettoId: z.string().max(80).optional(),
  da: dataIso.optional(),
  a: dataIso.optional(),
});
export const verificaCatenaSchema = z.object({ integra: z.boolean(), eventi: z.number().int(), primoErroreId: id.nullable(), verificataAt: dataIso });

// ---------------------------------------------------------------- GDPR: export e cancellazione
export const STATI_RICHIESTA_CANCELLAZIONE = ['in_attesa', 'approvata', 'rifiutata'] as const;
export const richiestaCancellazioneInputSchema = z.object({ motivo: z.string().trim().max(500).optional().or(z.literal('')), password: z.string().min(1, 'Inserisci la password per confermare.') });
export const richiestaCancellazioneSchema = z.object({
  id,
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  motivo: z.string().nullable(),
  stato: z.enum(STATI_RICHIESTA_CANCELLAZIONE),
  richiestaAt: dataIso,
  decisaAt: dataIso.nullable(),
  decisaDa: z.string().nullable(),
  nota: z.string().nullable(),
});
export type RichiestaCancellazione = z.infer<typeof richiestaCancellazioneSchema>;
export const decisioneCancellazioneSchema = z.object({ nota: z.string().trim().min(3, 'Scrivi una nota per il registro.').max(500) });
/** Stato della propria richiesta, visto dal partecipante. */
export const miaCancellazioneSchema = z.object({ stato: z.enum(STATI_RICHIESTA_CANCELLAZIONE).nullable(), richiestaAt: dataIso.nullable() });

// ---------------------------------------------------------------- TOTP (amministratori)
export const totpAvvioSchema = z.object({ segreto: z.string(), otpauthUrl: z.string(), qrDataUrl: z.string() });
export const totpCodiceSchema = z.object({ codice: z.string().trim().regex(/^\d{6}$/, 'Il codice ha 6 cifre.') });
export const totpDisattivaSchema = z.object({ password: passwordSchema.or(z.string().min(1)), codice: z.string().trim().regex(/^\d{6}$/) });
export const totpAccessoSchema = z.object({ email: emailSchema, codice: z.string().trim().regex(/^\d{6}$/, 'Il codice ha 6 cifre.') });
